import { Plus, Calendar } from "lucide-react";
import { ymd } from "./utils";

export function MobileDayList({ date, items, clientOf, staffOf, servicesOf, chipColor, onEdit, openForm, openHours, groupByStaff = false, staff = [] }) {
  const sorted = items.slice().sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  const dayCfg = (() => {
    const dow = new Date(date + "T00:00:00Z").getUTCDay();
    const key = String(dow === 0 ? 6 : dow - 1);
    return openHours[key] || { closed: true };
  })();
  const isPast = date < ymd(new Date());
  const isToday = date === ymd(new Date());

  // Raggruppamenti — per staff se richiesto, altrimenti per "fascia oraria"
  const groups = (() => {
    if (groupByStaff) {
      const byId = {};
      sorted.forEach((a) => {
        const sid = a.staff_id || "unassigned";
        (byId[sid] = byId[sid] || []).push(a);
      });
      return Object.entries(byId).map(([sid, list]) => ({
        key: sid,
        label: staffOf(sid)?.name || (sid === "unassigned" ? "Non assegnato" : "?"),
        color: staffOf(sid)?.color || "#94A3B8",
        list,
      }));
    }
    // Raggruppa per fascia oraria (mattina/pomeriggio/sera)
    const buckets = { morning: [], afternoon: [], evening: [] };
    sorted.forEach((a) => {
      const h = parseInt((a.start || "00:00").slice(11, 13), 10);
      if (h < 12) buckets.morning.push(a);
      else if (h < 17) buckets.afternoon.push(a);
      else buckets.evening.push(a);
    });
    return [
      { key: "morning",   label: "Mattina",    list: buckets.morning },
      { key: "afternoon", label: "Pomeriggio", list: buckets.afternoon },
      { key: "evening",   label: "Sera",       list: buckets.evening },
    ].filter((g) => g.list.length > 0);
  })();

  return (
    <div className="space-y-4" data-testid="mobile-day-list">
      {/* Riepilogo giorno */}
      <div className="sp-card p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] label-eyebrow">
            {isToday ? "Oggi" : isPast ? "Storico" : "In arrivo"}
          </div>
          <div className="font-display font-black text-lg mt-0.5">{sorted.length} appuntamenti</div>
        </div>
        {dayCfg.closed ? (
          <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-widest">Chiuso</span>
        ) : (
          <div className="text-[10px] text-[color:var(--sp-text-muted)] text-right tabular-nums">
            {dayCfg.open}–{dayCfg.close}
            {dayCfg.open2 && <><br />{dayCfg.open2}–{dayCfg.close2}</>}
          </div>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="sp-card p-8 text-center text-sm text-[color:var(--sp-text-muted)]">
          <Calendar className="h-8 w-8 mx-auto text-[color:var(--sp-border-strong)] mb-3" />
          Nessun appuntamento in agenda per questo giorno.
          <div className="mt-3">
            <button onClick={() => openForm()} data-testid="mobile-day-new-btn"
                    className="sp-btn-primary text-xs inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Aggiungi
            </button>
          </div>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key}>
            <div className="flex items-center gap-2 mb-2 px-1">
              {g.color && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: g.color }} />}
              <span className="label-eyebrow text-[10px] flex-1">{g.label}</span>
              <span className="text-[10px] text-[color:var(--sp-text-muted)]">{g.list.length}</span>
            </div>
            <div className="space-y-2">
              {g.list.map((a) => {
                const svcs = servicesOf(a);
                const client = clientOf(a.client_id);
                const staffName = staffOf(a.staff_id)?.name || "";
                const start = a.start?.slice(11, 16) || "";
                const end = a.end?.slice(11, 16) || "";
                const isCompleted = a.status === "completed";
                const isCancelled = a.status === "cancelled" || a.status === "no_show";
                return (
                  <button key={a.id} onClick={() => onEdit(a)} data-testid={`mobile-appt-${a.id}`}
                          className="sp-card p-3 w-full text-left flex items-stretch gap-3 hover:shadow-lg transition-shadow">
                    <div className="w-1.5 rounded-full flex-shrink-0" style={{ background: chipColor(a) }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-bold text-sm truncate flex-1">{client?.name || "Cliente"}</div>
                        <div className="tabular-nums text-xs font-black text-[color:var(--sp-text-secondary)] flex-shrink-0">
                          {start}<span className="text-[color:var(--sp-text-muted)] mx-0.5">–</span>{end}
                        </div>
                      </div>
                      <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-0.5 truncate">
                        {svcs.map((s) => s.name).join(" · ") || "Nessun servizio"}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {!groupByStaff && staffName && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-secondary)] font-bold">
                            {staffName}
                          </span>
                        )}
                        {isCompleted && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase tracking-widest">Completato</span>
                        )}
                        {isCancelled && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold uppercase tracking-widest">{a.status === "no_show" ? "No-show" : "Annullato"}</span>
                        )}
                        {a.notes && (
                          <span className="text-[10px] text-[color:var(--sp-text-muted)] truncate">📝 {a.notes.slice(0, 30)}{a.notes.length > 30 ? "…" : ""}</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
